import {
    StationView,
    MapGpsTypeEnumType,
    MapFullCompositeView,
    SubdivisionOption,
    AgencyProfileOption,
    AmlLocation,
} from '@mark43/cad-api';
import { Graphic, Popup, WebMap as WebMapConstructor } from './constructors';
import { EsriBasemapsIds, EsriAlternativeBasemapsIds } from './configurations';
import { NearbyGeoCautionDto } from '@mark43/rms-api';
import type { GeoPoint, MarginBounds, LocationStoreView } from '../location/types';

export type Layer = __esri.Layer;
export type FeatureLayerProperties = __esri.FeatureLayerProperties;
export type LabelClassProperties = __esri.LabelClassProperties;
export type FontProperties = __esri.FontProperties;
export type RendererProperties = __esri.RendererProperties;
export type GeometryProperties = __esri.GeometryProperties;
export type GraphicHit = __esri.GraphicHit;
export type GraphicProperties = __esri.GraphicProperties;
export type FeatureReductionClusterProperties = __esri.FeatureReductionClusterProperties;
export type FeatureLayerView = __esri.FeatureLayerView;
export type HitTestResult = __esri.HitTestResult;
export type ViewPointerMoveEvent = __esri.ViewPointerMoveEvent;
export type MapView = __esri.MapView;
export type FeatureLayer = __esri.FeatureLayer;
export type WebMap = __esri.WebMap;

export type CustomPopup = Popup & {
    domNode: Element;
};

export type MapOnChangeParams = {
    center: GeoPoint;
    zoom: number;
    marginBounds: MarginBounds;
};

/** Where a map is looking: enough to build a new view in the same place. */
export type MapViewpoint = {
    center: GeoPoint;
    zoom: number;
};

/**
 * Which UI a MapView belongs to. Every MapView declares one, and it is stamped onto
 * the container as `data-map-surface` so a leaked WebGL context can be traced back to
 * the UI that created it (stack traces cannot — Esri, not us, calls `getContext`).
 *
 * Add a value here when a new UI mounts a map. `main-map` is the only non-embedded
 * surface; everything else is an embedded map that mounts and unmounts with a page.
 */
export type MapSurface =
    | 'main-map'
    | 'fr-event-preview'
    | 'location-select'
    | 'location-select-v1'
    | 'command-line-location-search'
    | 'location-polygon-search'
    | 'location-radius-search';

/** The `main-map` surface is the only one driven by `useEsriBaseMap`. */
export const MAIN_MAP_SURFACE: MapSurface = 'main-map';

export const MAP_SURFACE_ATTRIBUTE = 'data-map-surface';

export type MapMode =
    | 'DARK_MODE'
    | 'LIGHT_MODE'
    | 'DARK_HUMAN_MODE'
    | 'LIGHT_HUMAN_MODE'
    | 'SATELLITE_MODE';

export type EsriBasemap = {
    mode: MapMode;
    id: EsriBasemapsIds | EsriAlternativeBasemapsIds;
    image: string;
    isPortalItem?: boolean;
};

export type IconUiStates = 'Inactive' | 'Selected';

export type UnitIconType =
    | 'Available'
    | 'On Foot'
    | 'On Foot Available'
    | 'Unavailable'
    | 'Unmanned';

export type UnitClass = 'Police' | 'Fire' | 'EMS';

export type UnitLocationSource = 'GPS' | 'GEO';

export enum EsriMapEntityTypeEnum {
    EVENT = 'EVENT',
    UNIT = 'UNIT',
    CLUSTER = 'CLUSTER',
    STATION = 'STATION',
    HOSPITAL = 'HOSPITAL',
    GEO_POINT_LOCATION = 'GEO_POINT_LOCATION',
    AML_CALL = 'AML_CALL',
    PHONE_CALL = 'PHONE_CALL',
    USER = 'USER',
    SENSOR = 'SENSOR',
}

export enum EsriMapContextMenuEntityTypeEnum {
    EVENT = 'EVENT',
    UNIT = 'UNIT',
}

export type LayerId = string | number;

export type HitTestCallback = (response: HitTestResult, event: any) => void;

/**
 * Why a map couldn't get WebGL2 when it started. `not-supported`: this page never had a
 * WebGL2 context. `stopped-working`: it had one earlier and can't get one now.
 */
export type WebGLUnavailableReason = 'not-supported' | 'stopped-working';

export interface EsriMapInterface {
    webMap: WebMapConstructor | undefined;
    mapView: MapView | undefined;
    /** Set when the map failed to start because it couldn't get WebGL2. */
    webGLUnavailable?: WebGLUnavailableReason;
    isMapLoading: boolean;
    isMapLoadingFailed: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
}

export type GeoPointLocation = LocationStoreView & { entityId: number };

export type StationViewEntity = StationView & { entityId: number };

export type ParsedEsriMapLocation = {
    index: number;
    type: string;
    rangeName: string;
    streetAddress: string;
    latitude: number;
    longitude: number;
};

export type CustomMapMarker = {
    id: string;
    label: string;
    letterIndex: string;
    location: {
        lat: number;
        lng: number;
    };
    tip: string;
};

export type FeatureLayerBaseProps = {
    mapView?: MapView;
    webMap?: WebMapConstructor;
    title: string;
    color: number[];
};

export type FeatureLayerApplyEdits = {
    addFeatures?: Graphic[];
    updateFeatures?: Graphic[];
    deleteFeatures?: Graphic[];
};

export type MapViewportUnitGraphicAttributes = {
    objectId?: number;
    entityId: number;
    callSign: string;
    iconName: string;
    type: MapGpsTypeEnumType;
    uiState: IconUiStates;
    eventPriorityString: string;
    agencyTypeGlobalAttrId: number;
};

export type PhoneCallGeoPointEntity = {
    entityId: number;
    latitude: number;
    longitude: number;
};

export type IndexedAmlLocationEntity = AmlLocation & { index: number; entityId: number };

export type AmlLocationGeoPointEntity = PhoneCallGeoPointEntity & {
    amlPhoneCallId?: number;
    uncertaintyRadius?: number;
    timestamp?: number;
};

export type CircleGeoPointEntity = {
    entityId: number;
    radius: number;
    latitude: number;
    longitude: number;
};

export type MapViewportEventGraphicAttributes = {
    objectId?: number;
    entityId: number;
    iconName: string;
    uiState: IconUiStates;
    priority: string;
    agencyTypeGlobalAttrId: number;
};

export type TimestampedGeoPoint = {
    entityId?: number;
    latitude?: number;
    longitude?: number;
    timestamp?: number;
};

export type MapViewportDerivedPolyline = {
    entityId: number;
    stringKey: string;
    type: string;
    visible?: boolean;
    path: GeoPoint[];
    geodesic?: boolean;
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
    icons?: {
        icon: { path: string; strokeOpacity: number; scale: number; strokeColor: string };
        offset: string;
        repeat: string;
    }[];
};

export type UnitUserLocation = {
    entityId: number; // user id
    gpsLocation?: TimestampedGeoPoint; // from gps
    isLocationPotentiallyInaccurate?: boolean;
};

export type FilterOption = { value: string; label: string };
export type DeprecatedEsriMapFilterOptions = {
    priorityOptions?: FilterOption[];
    dispatchAreaOptions?: FilterOption[];
    summaryCategoryOptions?: FilterOption[];
    subdivisionOptions?: FilterOption[];
    primarySubdivisionName?: string;
    agencyTypeIdToDisplay?: string;
    agencyTypeOptions?: FilterOption[];
};
export type EsriMapFilterOptions = {
    priorityOptions?: MapFullCompositeView['priorityOptions'];
    dispatchAreaOptions?: MapFullCompositeView['dispatchAreaOptions'];
    unitStatusOptions?: MapFullCompositeView['unitStatusOptions'];
    subdivisionOptions?: SubdivisionOption[];
    primarySubdivisionName?: string;
    agencyTypeIdToDisplay?: string;
    agencyTypeOptions?: MapFullCompositeView['agencyTypeOptions'];
    agencyProfileOptions?: AgencyProfileOption[];
};

export type DeprecatedEsriMapFilterState = {
    priorities?: string[];
    dispatchAreas?: string[];
    summaryCategories?: string[];
    subdivisions?: string[];
    agencyTypesGlobalAttributeIds?: string[];
};
export type EsriMapFilterState = {
    priorityFilters?: number[];
    dispatchAreaFilters?: number[];
    mapUnitStatusFilters?: number[];
    primarySubdivisionFilters?: number[];
    agencyTypeFilters?: number[];
    agencyProfileFilters?: number[];
};
export type EsriMapLayerOption = {
    value: string;
    label: string;
};

export type NearbyGeoCautionDtoExtended = NearbyGeoCautionDto & { entityId: number };
export type GeoPointExtended = GeoPoint & { entityId: number };
